import { Auth, API } from 'aws-amplify';
import { Link, useHistory } from 'react-router-dom';
import React, { useContext, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import { t } from 'i18next';

import { flatten } from '../../utils/flatten';
import ChangePassword from './ChangePassword';
import { defaultLanding, authentication } from '../../utils/enums';
import LabeledInput from '../shared/LabeledInput';
// TODO Removed for 1.0.0, replace once Spanish translations are finished.
// import LanguageSelector from '../shared/LanguageSelector';
import NextButton from '../shared/NextButton';
import Password from '../shared/Password';
import VerificationCode from './VerificationCode';
import { ShipyardContext } from '../Context';

import './Login.scss';

function Login() {
    const history = useHistory();
    const { user, userSettings, updateUser, updateUserSettings } = useContext(ShipyardContext);
    const [changePasswordStep, setChangePasswordStep] = React.useState(false);
    const [checkingCreds, setCheckingCreds] = React.useState(false);
    const [verificationCodeStep, setVerificationCodeStep] = React.useState(false);
    const [unconfirmedStep, setUnconfirmedStep] = React.useState(false);
    const [mfaChallengeType, setMfaChallengeType] = React.useState('');
    const [doingAjax, setDoingAjax] = React.useState(false);
    const [blankEmail, setBlankEmail] = React.useState(true);
    const [blankPassword, setBlankPassword] = React.useState(true);

    const emailFieldRef = useRef();
    const passwordFieldRef = useRef();

    const [inputEmail, setInputEmail] = React.useState('');
    const [inputPassword, setInputPassword] = React.useState('');

    useEffect(() => {
        if (isNaN(userSettings.ClientOrgId) || userSettings.ClientOrgId < 1) {
            history.push('/lnh');
        }
    }, []);

    useEffect(() => {
        let interval = setInterval(() => {
            if (emailFieldRef.current) {
                setBlankEmail(emailFieldRef?.current?.value?.length === 0);
                setBlankPassword(passwordFieldRef?.current?.value?.length === 0);
                clearInterval(interval);
            }
        }, 100);
    });

    const checkCreds = () =>
        new Promise((resolve, reject) => {
            setInputEmail(emailFieldRef.current?.value);
            setInputPassword(passwordFieldRef.current?.value);
            Auth.signIn(emailFieldRef.current?.value, passwordFieldRef.current?.value)
                .then((cognitoUser) => {
                    resolve(cognitoUser);
                })
                .catch((e) => {
                    reject(e);
                });
        });

    const handleNewPassword = (pwd) => {
        setDoingAjax(true);
        Auth.completeNewPassword(user, pwd, {
            given_name: user.challengeParam.userAttributes['given_name'] || 'N/A',
            family_name: user.challengeParam.userAttributes['family_name'] || 'N/A',
        })
            .then(async (loggedInUser) => {
                if (!loggedInUser) {
                    throw new Error();
                }
                // Password reset successful
                if (!loggedInUser.challengeName) {
                    // No further challenges. Redirect to Dashboard
                    toast.success(t('Authentication.PasswordChanged'));
                    await postAuth();
                } else {
                    // User must complete challenge
                    switch (loggedInUser.challengeName.toUpperCase()) {
                        case authentication.challenge.SMS:
                            setMfaChallengeType(authentication.challenge.SMS);
                            setVerificationCodeStep(true);
                            setChangePasswordStep(false);
                            break;

                        default: //TODO check other challenge types
                            console.error('Unknown challenge name', loggedInUser.challengeName);
                            throw new Error();
                    }
                }
                setDoingAjax(false);
            })
            .catch((e) => {
                setDoingAjax(false);
                switch (e?.code) {
                    case 'InvalidPasswordException':
                        toast.error(t('Authentication.PasswordWeak'));
                        break;
                    case 'NotAuthorizedException':
                        toast.error(t('Authentication.CreateAccount.SessionExpired'));
                        setChangePasswordStep(false);
                        break;
                    default:
                        toast.error(!!e?.message ? e.message : t('General.AjaxError'));
                        break;
                }
            });
    };

    const handleVerifySubmit = async (code) => {
        if (verificationCodeStep) {
            try {
                await Auth.confirmSignIn(user, code, mfaChallengeType);
                await postAuth();
                return;
            } catch (err) {
                if (err.code === 'CodeMismatchException') {
                    toast.error(t('Authentication.VerificationCodeIncorrect'));
                    return;
                }
                if (err.message.toLowerCase().includes('session is expired')) {
                    toast.error(t('General.AutoLogout'));
                    setVerificationCodeStep(false);
                    setCheckingCreds(false);
                    return;
                }
                console.error('Encountered unexpected error while confirming MFA challenge.');
                console.error(err);
                return;
            }
        }
        setDoingAjax(true);
        //Confirming new account
        try {
            await Auth.confirmSignUp(userSettings?.UserName, code);
            toast.success(t('Authentication.AccountVerified'));
            history.push('/dashboard');
        } catch (confirmationError) {
            console.error('Shipyard: Account confirmation error', confirmationError);

            let translatedErrorMsg = t(`Cognito.${confirmationError.code}`);

            toast.error(translatedErrorMsg !== `Cognito.${confirmationError.code}` ? translatedErrorMsg : confirmationError.message);
        } finally {
            setDoingAjax(false);
        }
    };

    /** Actions to perform upon successful authentication. */
    const postAuth = async () => {
        try {
            let userData = await Auth.currentUserInfo();

            toast.info(t('General.RetrievingUserData'));
            let response = await API.get('ShipyardAPI', '/usersettings', {
                queryStringParameters: {
                    login: true,
                },
            });
            let newUserSettingsState = {
                UserName: userData.attributes.email,
                PhoneNumber: userData.attributes.phone_number,
                Retrieved: true,
            };
            newUserSettingsState = Object.assign(newUserSettingsState, flatten(response));
            updateUserSettings((prev) => {
                return { ...prev, ...newUserSettingsState };
            });
            setCheckingCreds(false);

            history.push(defaultLanding(newUserSettingsState.UserType));
        } catch (e) {
            toast.error(t('General.AjaxError'));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (checkingCreds) return;
        else if (blankEmail && blankPassword) {
            toast.error(t('Authentication.EnterEmailAndPassword'));
            return;
        } else if (blankEmail) {
            toast.error(t('Authentication.EnterEmail'));
            return;
        } else if (blankPassword) {
            toast.error(t('Authentication.EnterPassword'));
            return;
        }

        setCheckingCreds(true);

        toast
            .promise(checkCreds, {
                pending: {
                    render() {
                        return t('Authentication.CheckingCredentials');
                    },
                },
                success: {
                    render({ data }) {
                        return t('General.Success');
                    },
                },
                error: {
                    render({ data: authErr }) {
                        var errText = t('Authentication.InvalidCredentials');
                        if (authErr.message && authErr.message.indexOf('attempts exceeded') > 0) {
                            errText = t('Authentication.AttemptsExceeded');
                        }
                        if (authErr.message && authErr.message.indexOf('not confirmed') > 0) {
                            errText = t('Authentication.AccountUnconfirmed');

                            let newUserSettingsState = {
                                UserName: emailFieldRef.current?.value,
                            };

                            updateUserSettings(newUserSettingsState);

                            setUnconfirmedStep(true);
                        }
                        return errText;
                    },
                },
            })
            .then(async (cognitoUser) => {
                updateUser(cognitoUser);
                if (cognitoUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
                    setCheckingCreds(false);
                    setChangePasswordStep(true);
                    return;
                }

                if (cognitoUser.challengeName) {
                    switch (cognitoUser.challengeName.toUpperCase()) {
                        case authentication.challenge.SMS:
                            setMfaChallengeType(authentication.challenge.SMS);
                            setVerificationCodeStep(true);
                            return;

                        default: //TODO check other challenge types
                            console.error('Unknown challenge name', cognitoUser.challengeName);
                            return;
                    }
                } else {
                    // No MFA challenge
                    await postAuth();
                }
            })
            .catch(() => {
                setCheckingCreds(false);
                if (emailFieldRef.current) emailFieldRef.current.focus();
            });
    };

    const handleResend = async () => {
        setDoingAjax(true);

        try {
            if (unconfirmedStep) {
                await Auth.resendSignUp(userSettings?.UserName);
            } else {
                let user = await Auth.signIn(inputEmail, inputPassword);
                updateUser(user);
            }

            toast.success(t(mfaChallengeType === authentication.challenge.SMS ? 'Cognito.MFAResendSuccess' : 'Cognito.ResendSuccess'));
        } catch (e) {
            toast.error(t('Cognito.ResendError'));
        } finally {
            setDoingAjax(false);
        }
    };

    const handleVerificationCodeCancel = () => {
        setUnconfirmedStep(false);
        setVerificationCodeStep(false);
        setCheckingCreds(false);
    };

    return (
        <div id="page-login">
            <div id="page-login-foreground">
                <h1>
                    <img
                        alt="Client Header"
                        src={userSettings.ClientHeaderImage || '/pcg-scholar.svg'}
                    />
                </h1>
                {(verificationCodeStep || unconfirmedStep) && (
                    <VerificationCode
                        callback={handleVerifySubmit}
                        cancelCallback={handleVerificationCodeCancel}
                        resendCallback={handleResend}
                        disabled={doingAjax}
                    />
                )}
                {changePasswordStep && (
                    <ChangePassword
                        callback={handleNewPassword}
                        disabled={doingAjax}
                    />
                )}
                {!verificationCodeStep && !changePasswordStep && !unconfirmedStep && (
                    <div id="form-login">
                        <div id="header-text">
                            <ReactMarkdown>{userSettings.ClientHeaderText}</ReactMarkdown>
                        </div>
                        <div role="main">
                            <form onSubmit={handleSubmit}>
                                <LabeledInput
                                    labelText={t('Authentication.Email')}
                                    placeholder={t('Authentication.EnterEmail')}
                                    disabled={checkingCreds}
                                    ref={emailFieldRef}
                                    autofocus={true}
                                    onChange={(val) => setBlankEmail(val.length === 0)}
                                    autoComplete="email"
                                    name="email"
                                />
                                <Password
                                    labelText={t('Authentication.Password')}
                                    placeholder={t('Authentication.EnterPassword')}
                                    innerRef={passwordFieldRef}
                                    disabled={checkingCreds}
                                    onChange={(val) => setBlankPassword(val.length === 0)}
                                    autoComplete="current-password"
                                    name="password"
                                />
                                <Link
                                    to="/forgotPassword"
                                    id="forgot-password"
                                >
                                    {t('Authentication.ForgotPasswordLink')}
                                </Link>
                                {/* 
                                    TODO Removed for 1.0.0 Release, replace once Spanish Translations are finished.
                                    <LanguageSelector /> 
                                */}
                                <NextButton
                                    action={handleSubmit}
                                    disabled={checkingCreds}
                                >
                                    {t('Authentication.LogIn')}
                                </NextButton>
                                <div id="not-registered">
                                    {t('Authentication.NotRegisteredYet')}
                                    <Link to="/signup">&nbsp;{t('Authentication.CreateAnAccountLink')}</Link>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
            <div id="page-login-background" />
        </div>
    );
}

export default Login;
